import { useInfiniteQuery } from '@tanstack/react-query'
import { Empty, Flex, SelectProps, Spin, Select } from 'antd'
import { useState } from 'react'

import { useStocksServiceGetFormListEndpointKey } from 'src/api/queries'
import { GetFormListResponseItem, StocksService } from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import { useDebounce } from 'src/hooks'

export const PharmaceuticalFormsSelect = (props: SelectProps) => {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)

  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: [useStocksServiceGetFormListEndpointKey, debouncedSearch],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await StocksService.getFormListEndpoint(
        pageParam,
        DEFAULT_PER_PAGE,
        undefined,
        debouncedSearch
      )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data: GetFormListResponseItem[] | undefined
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const options = data?.pages.flatMap(
    (page) =>
      page.data?.map((item: GetFormListResponseItem) => ({
        value: item.id,
        label: item.name
      })) || []
  )

  return (
    <Select
      allowClear
      popupMatchSelectWidth={true}
      filterOption={false}
      options={options}
      onSearch={(value) => setSearch(value)}
      onClear={() => setSearch('')}
      loading={isFetching}
      showSearch={true}
      onPopupScroll={(e) => {
        const target = e.target as HTMLElement
        if (target.scrollTop + target.offsetHeight === target.scrollHeight)
          fetchNextPage()
      }}
      notFoundContent={
        isFetching ? (
          <Flex justify="center">
            <Spin spinning />
          </Flex>
        ) : (
          <Empty />
        )
      }
      {...props}
    />
  )
}
